import type {
  DocumentNode,
  ObservableQuery,
  OperationVariables,
  WatchQueryFetchPolicy,
} from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import * as Sentry from '@sentry/core';
import { useEffect } from 'react';

import { sendSafeError } from '#src/lib/error.ts';
import { IncompleteCacheReadError } from '#src/lib/fingerprintedErrors.ts';

// Apollo writes a network result to the cache and reads it back; only a
// complete read-back is used. An incomplete one is kept as the raw network
// result, which silently skips every `read` policy in the query, so `Date`
// fields arrive as ISO strings and cryptocurrency amounts as strings. Apollo
// names the missing field in a warning that only exists in development builds.
// `cache.diff` runs the same comparison in production. See
// docs/knowledge-vault/concepts/observability/apollo-read-policy-bypass-playbook.md
const reportedOperations = new Set<string>();

const operationName = (query: DocumentNode) => {
  for (const definition of query.definitions) {
    if (definition.kind === 'OperationDefinition' && definition.name) {
      return definition.name.value;
    }
  }
  return undefined;
};

type MissingTree = string | { [key: string]: MissingTree };

const missingFieldPaths = (
  tree: MissingTree | undefined,
  prefix: string[] = []
): string[] => {
  if (!tree) return [];
  if (typeof tree === 'string') return [prefix.join('.')];

  return Object.entries(tree).flatMap(([key, subtree]) =>
    missingFieldPaths(subtree, [...prefix, key])
  );
};

export const useReportIncompleteCacheRead = <
  TVariables extends OperationVariables,
>({
  query,
  variables,
  fetchPolicy,
  observable,
  hasData,
  loading,
}: {
  query: DocumentNode;
  variables: TVariables | undefined;
  fetchPolicy: WatchQueryFetchPolicy | undefined;
  observable: Pick<ObservableQuery, 'getCurrentResult'>;
  hasData: boolean;
  loading: boolean;
}) => {
  const client = useApolloClient();

  useEffect(() => {
    const resultWrittenToCache = fetchPolicy !== 'no-cache';
    if (!resultWrittenToCache || !hasData || loading) return;

    // `clearStore`/`resetStore` (e.g. on log out) reset every ObservableQuery
    // without emitting, so the component still renders the pre-reset data
    // while the cache is empty. Only the ObservableQuery knows it was dropped.
    const resultDroppedByStoreReset = !observable.getCurrentResult().data;
    if (resultDroppedByStoreReset) return;

    const name = operationName(query);
    if (!name || reportedOperations.has(name)) return;

    const { complete, missing } = client.cache.diff({
      query,
      variables,
      optimistic: true,
      returnPartialData: true,
    });
    if (complete) return;

    const paths = missingFieldPaths(
      (missing as { path?: MissingTree } | undefined)?.path
    );
    if (!paths.length) return;

    reportedOperations.add(name);

    Sentry.withScope(scope => {
      scope.setTag('kind', name);
      scope.setExtra('missingFields', paths);
      scope.setFingerprint(['incomplete-cache-readback', name]);
      sendSafeError(
        new IncompleteCacheReadError(
          `Incomplete cache read-back in ${name}: ${paths.join(', ')}`
        )
      );
    });
  }, [client, query, variables, fetchPolicy, observable, hasData, loading]);
};
